import * as React from 'react';
import { ApiHatasi, api } from '@/lib/api';
import { anMs } from '@/lib/utils';

/**
 * KBS durum ekrani.
 *
 * Tasarim kaynagi: Claude Design — "otelcms KBS Durum.dc.html".
 * Tasarimdaki satirlar ureticiydi; buradaki her sey gercek uclardan gelir:
 *   GET  /api/kbs/durum                          -> ozet sayilar
 *   GET  /api/kbs/bildirimler                    -> satirlar
 *   POST /api/kbs/bildirimler/:id/tekrar-dene    -> yeniden kuyruga al
 *   POST /api/kbs/bildirimler/:id/duzelt-gonder  -> eksik alani tamamla + gonder
 */

type KbsDurum = 'BEKLIYOR' | 'GONDERILIYOR' | 'GONDERILDI' | 'HATA' | 'SURESI_DOLMAK_UZERE' | 'IPTAL';

interface Kayit {
  id: string;
  durum: KbsDurum;
  islemTuru: string;
  deneme: number;
  sonHataKodu: string | null;
  sonHataMesaji: string | null;
  sonGonderimAni: string | number;
  gonderimTarihi: string | number | null;
  rezervasyonKodu: string | null;
  girisTarihi: string | null;
  odaNo: string | null;
  guestId: string;
  misafirAd: string | null;
  misafirSoyad: string | null;
  uyruk: string | null;
}
type Ozet = Record<KbsDurum, number>;

const DURUM_BILGI: Record<KbsDurum, { etiket: string; renk: string }> = {
  GONDERILDI: { etiket: 'Gönderildi', renk: '#12a150' },
  BEKLIYOR: { etiket: 'Sırada', renk: '#b9840f' },
  GONDERILIYOR: { etiket: 'Gönderiliyor', renk: '#1f4fd8' },
  SURESI_DOLMAK_UZERE: { etiket: 'Süresi doluyor', renk: '#e5484d' },
  HATA: { etiket: 'Hata', renk: '#e5484d' },
  IPTAL: { etiket: 'İptal', renk: '#5b6480' },
};

const SUZGECLER = [
  { anahtar: 'hepsi', etiket: 'Tümü' },
  { anahtar: 'bekleyen', etiket: 'Bekleyen' },
  { anahtar: 'gonderilen', etiket: 'Gönderilen' },
  { anahtar: 'hatali', etiket: 'Hatalı' },
] as const;
type Suzgec = (typeof SUZGECLER)[number]['anahtar'];

const BEKLEYEN: KbsDurum[] = ['BEKLIYOR', 'GONDERILIYOR', 'SURESI_DOLMAK_UZERE'];
const bassHarf = (a: string | null, s: string | null) => `${(a ?? '?')[0] ?? ''}${(s ?? '')[0] ?? ''}`.toUpperCase();
const saat = (an: string | number | null) => {
  const ms = anMs(an);
  return ms ? new Date(ms).toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' }) : '';
};
const kisaTarih = (iso: string | null) =>
  iso ? new Date(`${iso}T00:00:00`).toLocaleDateString('tr-TR', { day: '2-digit', month: '2-digit' }) : '—';

export function KbsPaneli({ yetkiler }: { yetkiler: string[] }) {
  const gonderebilir = yetkiler.includes('kbs:retry');
  const duzeltebilir = gonderebilir && yetkiler.includes('identity:read');

  const [ozet, setOzet] = React.useState<Ozet | null>(null);
  const [kayitlar, setKayitlar] = React.useState<Kayit[]>([]);
  const [suzgec, setSuzgec] = React.useState<Suzgec>('hepsi');
  const [yukleniyor, setYukleniyor] = React.useState(true);
  const [hata, setHata] = React.useState<string | null>(null);
  const [islenen, setIslenen] = React.useState<Set<string>>(new Set());
  const [duzeltilen, setDuzeltilen] = React.useState<Kayit | null>(null);
  const [simdi, setSimdi] = React.useState(() => new Date());

  React.useEffect(() => {
    const t = setInterval(() => setSimdi(new Date()), 1000);
    return () => clearInterval(t);
  }, []);

  const yukle = React.useCallback(async () => {
    try {
      const [d, l] = await Promise.all([
        api<{ ozet: Ozet }>('/api/kbs/durum'),
        api<{ kayitlar: Kayit[] }>('/api/kbs/bildirimler?limit=200'),
      ]);
      setOzet(d.ozet);
      setKayitlar(l.kayitlar);
      setHata(null);
    } catch (e) {
      setHata(e instanceof ApiHatasi ? e.message : 'Bildirimler alınamadı.');
    } finally {
      setYukleniyor(false);
    }
  }, []);

  React.useEffect(() => {
    void yukle();
    const t = setInterval(() => void yukle(), 20_000);
    return () => clearInterval(t);
  }, [yukle]);

  async function gonder(id: string) {
    setIslenen((o) => new Set(o).add(id));
    try {
      await api(`/api/kbs/bildirimler/${id}/tekrar-dene`, { method: 'POST' });
      await yukle();
    } catch (e) {
      setHata(e instanceof ApiHatasi ? e.message : 'Gönderilemedi.');
    } finally {
      setIslenen((o) => {
        const y = new Set(o);
        y.delete(id);
        return y;
      });
    }
  }

  async function hepsiniGonder() {
    const hedef = kayitlar.filter((k) => k.durum === 'BEKLIYOR' || k.durum === 'SURESI_DOLMAK_UZERE');
    for (const k of hedef) await gonder(k.id);
  }

  const say = (s: Suzgec) =>
    kayitlar.filter((k) =>
      s === 'hepsi' ? true : s === 'bekleyen' ? BEKLEYEN.includes(k.durum) : s === 'gonderilen' ? k.durum === 'GONDERILDI' : k.durum === 'HATA',
    ).length;

  const gosterilen = kayitlar.filter((k) =>
    suzgec === 'hepsi'
      ? true
      : suzgec === 'bekleyen'
        ? BEKLEYEN.includes(k.durum)
        : suzgec === 'gonderilen'
          ? k.durum === 'GONDERILDI'
          : k.durum === 'HATA',
  );

  const toplam = kayitlar.length || 1;
  const gonderilen = kayitlar.filter((k) => k.durum === 'GONDERILDI').length;
  const yuzde = Math.round((gonderilen / toplam) * 100);
  const C = 226;

  const olaylar = kayitlar
    .filter((k) => k.gonderimTarihi || k.durum === 'HATA')
    .sort((a, b) => (anMs(b.gonderimTarihi) ?? 0) - (anMs(a.gonderimTarihi) ?? 0))
    .slice(0, 6);

  return (
    <main className="flex flex-1 flex-col gap-[22px] bg-zemin p-5 sm:p-7">
      <div className="flex flex-wrap items-center gap-3.5 [animation:rise_.5s_cubic-bezier(.2,.7,.2,1)_both]">
        <h1 className="text-3xl font-bold tracking-[-.03em]">KBS durum</h1>
        <span className="flex items-center gap-2 rounded-full bg-yesil-zemin px-3 py-1.5 text-[13px] font-semibold text-yesil">
          <span className="relative size-2">
            <span className="absolute inset-0 rounded-full bg-yesil" />
            <span className="absolute inset-0 rounded-full border-[1.5px] border-yesil [animation:ring_1.8s_ease-out_infinite]" />
          </span>
          Bağlantı canlı · {simdi.toLocaleTimeString('tr-TR')}
        </span>

        <div className="flex w-full gap-1.5 rounded-full border border-cizgi bg-white p-1 sm:ml-auto sm:w-auto overflow-x-auto [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
          {SUZGECLER.map((f) => (
            <button
              key={f.anahtar}
              onClick={() => setSuzgec(f.anahtar)}
              className={
                'flex items-center gap-[7px] whitespace-nowrap rounded-full px-3.5 py-[7px] text-[13px] font-semibold transition-colors ' +
                (suzgec === f.anahtar ? 'bg-lacivert text-white' : 'text-gri hover:text-lacivert')
              }
            >
              {f.etiket}
              <span
                className={
                  'rounded-full px-[7px] py-px text-[11px] ' +
                  (suzgec === f.anahtar ? 'bg-white/20 text-white' : 'bg-satir text-gri')
                }
              >
                {say(f.anahtar)}
              </span>
            </button>
          ))}
        </div>

        {gonderebilir && (
          <button
            onClick={hepsiniGonder}
            disabled={say('bekleyen') === 0 || islenen.size > 0}
            className="flex items-center gap-2 rounded-full bg-marka-600 px-[18px] py-[11px] text-sm font-semibold text-white shadow-[0_10px_24px_-12px_rgba(31,79,216,.7)] transition hover:-translate-y-px hover:bg-lacivert disabled:opacity-40 disabled:hover:translate-y-0 disabled:hover:bg-marka-600"
          >
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="[animation:fly_2.4s_ease-in-out_infinite]">
              <path d="M2 8l12-6-4 12-2-5-6-1z" />
            </svg>
            Sıradakileri gönder
          </button>
        )}
      </div>

      {hata && <div className="rounded-xl border border-red-300 bg-red-50 px-4 py-3 text-red-800">{hata}</div>}

      <div className="grid gap-3.5 [animation:rise_.6s_.05s_cubic-bezier(.2,.7,.2,1)_both] sm:grid-cols-2 xl:grid-cols-4">
        <div className="flex items-center gap-5 rounded-[18px] bg-lacivert p-5 text-white">
          <svg width="86" height="86" viewBox="0 0 86 86" className="shrink-0 -rotate-90">
            <circle cx="43" cy="43" r="36" fill="none" stroke="rgba(255,255,255,.12)" strokeWidth="8" />
            <circle
              cx="43" cy="43" r="36" fill="none" stroke="#5fd48f" strokeWidth="8" strokeLinecap="round"
              strokeDasharray={C} strokeDashoffset={C - (C * yuzde) / 100}
              style={{ transition: 'stroke-dashoffset 1s cubic-bezier(.2,.8,.2,1)' }}
            />
          </svg>
          <div className="flex flex-col gap-1">
            <span className="text-xs font-semibold uppercase tracking-[.08em] text-mavi-acik">Bildirilen</span>
            <span className="text-[34px] font-bold leading-none tracking-[-.03em]">%{yuzde}</span>
            <span className="text-[13px] text-[#b6c2e2]">
              {kayitlar.length} bildirimden {gonderilen} gönderildi
            </span>
          </div>
        </div>

        {([
          ['Sırada', ozet?.BEKLIYOR ?? 0, '#b9840f', 'otomatik gidecek'],
          ['Süresi dolmak üzere', ozet?.SURESI_DOLMAK_UZERE ?? 0, '#e5484d', 'öncelikli'],
          ['Hatalı', ozet?.HATA ?? 0, '#e5484d', 'düzeltme bekliyor'],
        ] as [string, number, string, string][]).map(([etiket, deger, renk, alt]) => (
          <div key={etiket} className="flex flex-col gap-2 rounded-[18px] border border-cizgi bg-white p-5">
            <span className="text-xs font-semibold uppercase tracking-[.08em] text-gri">{etiket}</span>
            <div className="flex items-baseline gap-2.5">
              <span className="text-[30px] font-bold leading-none tracking-[-.03em]" style={{ color: renk }}>
                {deger}
              </span>
              <span className="text-[13px] text-gri">{alt}</span>
            </div>
            <div className="h-1 overflow-hidden rounded-sm bg-satir">
              <div
                className="h-full origin-left"
                style={{ width: `${Math.round((deger / toplam) * 100)}%`, background: renk, animation: 'bar 1s .3s cubic-bezier(.2,.8,.2,1) both' }}
              />
            </div>
          </div>
        ))}
      </div>

      <div className="grid items-start gap-[18px] xl:grid-cols-[minmax(0,1fr)_320px]">
        <div className="overflow-hidden rounded-[20px] border border-cizgi bg-white shadow-[0_30px_60px_-40px_rgba(11,21,48,.25)] [animation:rise_.6s_.1s_cubic-bezier(.2,.7,.2,1)_both]">
          <div className="grid grid-cols-[1.4fr_.6fr_.8fr_1.2fr_140px] gap-3 border-b border-cizgi bg-[#fbfcfe] px-5 py-3 text-xs font-bold uppercase tracking-[.06em] text-gri">
            <span>Misafir</span><span>Oda</span><span>Belge</span><span>Durum</span><span />
          </div>

          {yukleniyor ? (
            [0, 1, 2, 3].map((i) => <div key={i} className="h-16 animate-pulse border-b border-satir bg-white" />)
          ) : gosterilen.length === 0 ? (
            <p className="px-5 py-12 text-center text-sm text-gri">
              {suzgec === 'hepsi' ? 'Henüz KBS bildirimi yok.' : 'Bu filtrede kayıt yok.'}
            </p>
          ) : (
            gosterilen.map((k, i) => {
              const bilgi = DURUM_BILGI[k.durum];
              const gonderilebilir = gonderebilir && (k.durum === 'BEKLIYOR' || k.durum === 'SURESI_DOLMAK_UZERE');
              const hataliMi = k.durum === 'HATA';
              return (
                <div
                  key={k.id}
                  className="grid grid-cols-[1.4fr_.6fr_.8fr_1.2fr_140px] items-center gap-3 border-b border-satir px-5 py-3.5 transition-colors hover:bg-[#f8faff] [animation:rise_.5s_both]"
                  style={{ animationDelay: `${i * 30}ms`, background: k.durum === 'SURESI_DOLMAK_UZERE' ? '#fff5f5' : undefined }}
                >
                  <div className="flex min-w-0 items-center gap-3">
                    <span className="grid size-9 shrink-0 place-items-center rounded-full bg-mavi-zemin text-[13px] font-bold text-marka-600">
                      {bassHarf(k.misafirAd, k.misafirSoyad)}
                    </span>
                    <div className="flex min-w-0 flex-col">
                      <span className="truncate font-semibold">
                        {k.misafirAd} {k.misafirSoyad}
                      </span>
                      <span className="text-xs text-gri">
                        {k.uyruk ?? '—'} · giriş {kisaTarih(k.girisTarihi)}
                      </span>
                    </div>
                  </div>
                  <span className="font-semibold">{k.odaNo ?? '—'}</span>
                  <span className="text-[13px] text-gri">{k.uyruk === 'TUR' ? 'TCKN' : 'Pasaport'}</span>

                  <div className="flex items-center gap-2 text-[13px] font-semibold" style={{ color: bilgi.renk }}>
                    {k.durum === 'GONDERILIYOR' && (
                      <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="2" className="[animation:spin_.9s_linear_infinite]">
                        <path d="M7 1.5a5.5 5.5 0 1 1-5.5 5.5" />
                      </svg>
                    )}
                    {k.durum === 'GONDERILDI' && (
                      <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <circle cx="7" cy="7" r="6" /><path d="M4.5 7l2 2 3-3.5" />
                      </svg>
                    )}
                    {k.durum === 'BEKLIYOR' && <span className="size-2 rounded-full bg-current [animation:blink_1.2s_infinite]" />}
                    {k.durum === 'SURESI_DOLMAK_UZERE' && (
                      <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" className="[animation:shake_1.4s_ease-in-out_infinite]">
                        <path d="M7 1.5L13 12.5H1z M7 5.5v3M7 10.5v.01" />
                      </svg>
                    )}
                    {hataliMi && (
                      <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                        <circle cx="7" cy="7" r="6" /><path d="M5 5l4 4M9 5l-4 4" />
                      </svg>
                    )}
                    <span className="truncate" title={k.sonHataMesaji ?? undefined}>
                      {hataliMi && k.sonHataMesaji ? k.sonHataMesaji : bilgi.etiket}
                    </span>
                  </div>

                  <div className="flex justify-end">
                    {k.durum === 'GONDERILDI' ? (
                      <span className="text-xs text-gri">{saat(k.gonderimTarihi)}</span>
                    ) : hataliMi && duzeltebilir ? (
                      <button
                        onClick={() => setDuzeltilen(k)}
                        className="whitespace-nowrap rounded-full border-[1.5px] border-lacivert bg-white px-3.5 py-[7px] text-[13px] font-semibold text-lacivert transition hover:bg-lacivert hover:text-white"
                      >
                        Düzelt ve gönder
                      </button>
                    ) : gonderilebilir ? (
                      <button
                        onClick={() => gonder(k.id)}
                        disabled={islenen.has(k.id)}
                        className="whitespace-nowrap rounded-full border-[1.5px] border-lacivert bg-white px-3.5 py-[7px] text-[13px] font-semibold text-lacivert transition hover:bg-lacivert hover:text-white disabled:opacity-50"
                      >
                        {islenen.has(k.id) ? 'Gönderiliyor…' : 'Şimdi gönder'}
                      </button>
                    ) : null}
                  </div>
                </div>
              );
            })
          )}
        </div>

        <aside className="flex flex-col gap-3.5 [animation:rise_.6s_.15s_cubic-bezier(.2,.7,.2,1)_both]">
          <div className="flex flex-col gap-3.5 rounded-[20px] border border-cizgi bg-white p-5">
            <div className="flex items-center justify-between">
              <h3 className="text-[15px] font-bold">Gönderim akışı</h3>
              <span className="text-xs text-gri">canlı</span>
            </div>
            <svg viewBox="0 0 280 90" width="100%" fill="none" strokeLinecap="round" strokeLinejoin="round">
              <rect x="6" y="28" width="60" height="36" rx="8" stroke="#0b1530" strokeWidth="2" fill="#fff" />
              <text x="36" y="51" textAnchor="middle" fontFamily="Space Grotesk" fontSize="10" fontWeight="700" fill="#0b1530">otelcms</text>
              <path d="M70 46h60" stroke="#1f4fd8" strokeWidth="2" strokeDasharray="6 6" className="[animation:dash_.8s_linear_infinite]" />
              <rect x="132" y="22" width="48" height="48" rx="12" stroke="#0b1530" strokeWidth="2" fill="#edf2ff" />
              <path d="M148 46l6 6 10-12" stroke="#1f4fd8" strokeWidth="2.5" />
              <path d="M184 46h60" stroke="#1f4fd8" strokeWidth="2" strokeDasharray="6 6" className="[animation:dash_.8s_linear_infinite]" />
              <rect x="216" y="28" width="60" height="36" rx="8" stroke="#0b1530" strokeWidth="2" fill="#0b1530" />
              <text x="246" y="51" textAnchor="middle" fontFamily="Space Grotesk" fontSize="11" fontWeight="700" fill="#fff">KBS</text>
              <text x="156" y="86" textAnchor="middle" fontFamily="Space Grotesk" fontSize="9" fill="#5b6480">şifreli · sabit IP</text>
            </svg>
            <p className="text-[13px] leading-relaxed text-gri">
              İnternet kesilirse bildirim cihazda bekler, bağlantı gelince kendiliğinden gider.
            </p>
          </div>

          <div className="flex flex-col gap-3 rounded-[20px] border border-cizgi bg-white p-5">
            <h3 className="text-[15px] font-bold">Son olaylar</h3>
            {olaylar.length === 0 ? (
              <p className="text-[13px] text-gri">Henüz olay yok.</p>
            ) : (
              olaylar.map((k) => (
                <div key={k.id} className="flex gap-2.5 text-[13px] [animation:rise_.4s_both]">
                  <span className="shrink-0 tabular-nums text-gri">{saat(k.gonderimTarihi) || '—'}</span>
                  <span style={{ color: k.durum === 'GONDERILDI' ? '#12a150' : '#e5484d' }}>
                    {[k.misafirAd, k.misafirSoyad].filter(Boolean).join(' ')} · Oda {k.odaNo ?? '—'} {k.durum === 'GONDERILDI' ? 'bildirildi' : 'hata'}
                  </span>
                </div>
              ))
            )}
          </div>
        </aside>
      </div>

      {duzeltilen && (
        <DuzeltPenceresi
          kayit={duzeltilen}
          onKapat={() => setDuzeltilen(null)}
          onTamam={async () => {
            setDuzeltilen(null);
            await yukle();
          }}
        />
      )}
    </main>
  );
}

/* ------------------------------------------------------- duzelt ve gonder */

const ALANLAR = [
  { anahtar: 'ad', etiket: 'Ad' },
  { anahtar: 'soyad', etiket: 'Soyad' },
  { anahtar: 'anneAdi', etiket: 'Anne adı' },
  { anahtar: 'babaAdi', etiket: 'Baba adı' },
  { anahtar: 'dogumTarihi', etiket: 'Doğum tarihi', tip: 'date' },
  { anahtar: 'dogumYeri', etiket: 'Doğum yeri' },
] as const;

function DuzeltPenceresi({
  kayit,
  onKapat,
  onTamam,
}: {
  kayit: Kayit;
  onKapat: () => void;
  onTamam: () => void | Promise<void>;
}) {
  const [deger, setDeger] = React.useState<Record<string, string>>({});
  const [cinsiyet, setCinsiyet] = React.useState('');
  const [gonderiliyor, setGonderiliyor] = React.useState(false);
  const [hata, setHata] = React.useState<string | null>(null);

  // Hata mesajindaki alan adlari sari isaretlenir
  const eksik = new Set(
    (kayit.sonHataMesaji ?? '').match(/\b(ad|soyad|anneAdi|babaAdi|dogumTarihi|dogumYeri|cinsiyet|tckn|pasaportNo|uyruk)\b/g) ?? [],
  );

  async function gonder(e: React.FormEvent) {
    e.preventDefault();
    setGonderiliyor(true);
    setHata(null);
    const govde: Record<string, string> = {};
    for (const [k, v] of Object.entries(deger)) if (v.trim()) govde[k] = v.trim();
    if (cinsiyet) govde.cinsiyet = cinsiyet;

    try {
      await api(`/api/kbs/bildirimler/${kayit.id}/duzelt-gonder`, { method: 'POST', body: JSON.stringify(govde) });
      await onTamam();
    } catch (err) {
      setHata(err instanceof ApiHatasi ? err.message : 'Gönderilemedi.');
      setGonderiliyor(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-lacivert/60 p-4" onClick={onKapat}>
      <form
        onClick={(e) => e.stopPropagation()}
        onSubmit={gonder}
        className="flex w-full max-w-lg flex-col gap-4 rounded-[20px] bg-white p-6 [animation:popin_.3s_cubic-bezier(.2,.8,.2,1)_both]"
      >
        <div>
          <h2 className="text-xl font-bold">Düzelt ve gönder</h2>
          <p className="mt-1 text-sm text-gri">
            {kayit.misafirAd} {kayit.misafirSoyad} · Oda {kayit.odaNo ?? '—'}
          </p>
        </div>

        {kayit.sonHataMesaji && (
          <div className="rounded-xl border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-900">
            {kayit.sonHataMesaji}
          </div>
        )}

        <div className="grid gap-3 sm:grid-cols-2">
          {ALANLAR.map((a) => (
            <label key={a.anahtar} className="flex flex-col gap-1.5">
              <span className="text-sm font-medium text-slate-700">
                {a.etiket}
                {eksik.has(a.anahtar) && <span className="ml-2 rounded-full bg-amber-100 px-2 py-0.5 text-xs font-semibold text-amber-800">eksik</span>}
              </span>
              <input
                type={'tip' in a ? a.tip : 'text'}
                value={deger[a.anahtar] ?? ''}
                onChange={(e) => setDeger((o) => ({ ...o, [a.anahtar]: e.target.value }))}
                className={
                  'h-11 rounded-lg border px-3 text-base focus:border-marka-600 focus:outline-none focus:ring-2 focus:ring-marka-100 ' +
                  (eksik.has(a.anahtar) ? 'border-amber-400 bg-amber-50' : 'border-slate-300')
                }
              />
            </label>
          ))}
          <label className="flex flex-col gap-1.5">
            <span className="text-sm font-medium text-slate-700">Cinsiyet</span>
            <select
              value={cinsiyet}
              onChange={(e) => setCinsiyet(e.target.value)}
              className="h-11 rounded-lg border border-slate-300 px-3 text-base focus:border-marka-600 focus:outline-none focus:ring-2 focus:ring-marka-100"
            >
              <option value="">Değiştirme</option>
              <option value="K">Kadın</option>
              <option value="E">Erkek</option>
            </select>
          </label>
        </div>

        <p className="text-xs text-gri">Yalnızca doldurduğunuz alanlar güncellenir; boş bıraktıklarınıza dokunulmaz.</p>
        {hata && <p className="text-sm text-red-600">{hata}</p>}

        <div className="flex justify-end gap-2">
          <button type="button" onClick={onKapat} className="rounded-full px-4 py-2.5 font-semibold text-gri hover:text-lacivert">
            Vazgeç
          </button>
          <button
            type="submit"
            disabled={gonderiliyor}
            className="rounded-full bg-marka-600 px-5 py-2.5 font-semibold text-white transition hover:bg-lacivert disabled:opacity-50"
          >
            {gonderiliyor ? 'Gönderiliyor…' : 'Düzelt ve gönder'}
          </button>
        </div>
      </form>
    </div>
  );
}
